import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, authPersistenceReady } from '../firebase';
import { updateUserDisplayName } from '../data/users';
import { LoginHeader } from '../components/login/LoginHeader';
import { LoginForm } from '../components/login/LoginForm';
import { LandingLayout } from '../components/landing/LandingLayout';
import { Footer } from '../components/landing/Footer';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authPersistenceReady;
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update display name if it's not set
      if (!user.displayName) {
        await updateUserDisplayName(user, email.split('@')[0]);
      }

      navigate('/dashboard');
    } catch (err) {
      setError('Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authPersistenceReady;
      await sendPasswordResetEmail(auth, email);
      setSuccess('Password reset email sent. Please check your inbox.');
    } catch (err) {
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LandingLayout>
      <Helmet>
        <title>Log In - BrainLoop</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          py: { xs: 8, md: 10 },
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.secondary.main} 100%)`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(ellipse at 28% 18%, ${alpha(theme.palette.common.white, 0.16)}, transparent 45%)`,
            filter: 'blur(60px)',
            opacity: 0.8,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(ellipse at 80% 10%, ${alpha(theme.palette.common.white, 0.12)}, transparent 38%)`,
            filter: 'blur(70px)',
            opacity: 0.65,
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              borderRadius: 4,
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.default, 0.82)
                : alpha(theme.palette.background.paper, 0.9),
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: theme.palette.mode === 'dark'
                ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
                : `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              boxShadow: theme.palette.mode === 'dark'
                ? [
                    `0 20px 60px ${alpha(theme.palette.common.black, 0.5)}`,
                    `0 8px 20px ${alpha(theme.palette.common.black, 0.3)}`,
                    `inset 0 2px 0 ${alpha(theme.palette.common.white, 0.05)}`,
                    `inset 0 -2px 0 ${alpha(theme.palette.common.black, 0.2)}`,
                  ].join(', ')
                : [
                    `0 20px 60px ${alpha(theme.palette.primary.main, 0.2)}`,
                    `0 8px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                    `inset 0 2px 0 ${alpha(theme.palette.common.white, 0.95)}`,
                    `inset 0 -2px 0 ${alpha(theme.palette.primary.main, 0.08)}`,
                  ].join(', '),
            }}
          >
            <LoginHeader onBackToHome={() => navigate('/')} />
            <LoginForm
              email={email}
              password={password}
              showPassword={showPassword}
              loading={loading}
              error={error}
              success={success}
              onEmailChange={(e) => setEmail(e.target.value)}
              onPasswordChange={(e) => setPassword(e.target.value)}
              onTogglePasswordVisibility={() => setShowPassword(!showPassword)}
              onSubmit={handleLogin}
              onForgotPassword={handleForgotPassword}
              onCreateAccount={() => navigate('/signup')}
            />
          </Paper>
        </Container>
      </Box>
      <Footer />
    </LandingLayout>
  );
} 